// [목적] 인증 페이지 전용 디자인 토큰 모듈 — AUTH_COLORS / AUTH_SPACING / AUTH_BORDER / AUTH_GRADIENTS 상수 제공
// [이유] packages/ui 디렉터리가 아직 부재(R-10)하여 인증 페이지 한정 로컬 SoT를 선행 마련.
//         LoginPage/AuthChrome/Onboarding 의 hex/spacing 값을 단일 파일에 집중시켜
//         향후 packages/ui 이관 시 import 경로만 치환하면 되도록 설계한다.
//         2026-05-16 reauth-redesign — docs/플리오랩/gangwonfolio-web.html 1:1 포팅으로
//         warm/earthy 팔레트를 다시 cool blue/teal 팔레트로 전면 교체.
//         이전 [[2026-05-15_brand-logo-and-color-palette]] Spec 의 warm 토큰은 폐기 (사용자 요청).
// [사이드 이팩트] 이 파일 자체가 hex/px 직접 정의 허용 예외 지점(CLAUDE.md §6-3 단서).
//                  이 파일 외 인증 페이지 코드에서 hex/px 직접 입력 금지.
//                  brandBlue / surfaceDark / brandTealLight 의미 재정의 — warm → cool blue 복원.
//                  이름 유지로 호출부 무중단 적용 (LoginDesktopLayout/MobileLayout/BrandVisualPanel/OAuthButtonStack).
//                  추가 export: AUTH_GRADIENTS (브랜드 패널·CTA 그라데이션 SoT).
// [수정 시 고려사항]
//   - default export 금지 — 명명 export 만 허용
//   - packages/ui 이관 시 export 명은 그대로 두고 import 경로만 교체
//   - OAuth 4종 토큰은 AC-3·Q-8 보호로 불변 (Kakao/Naver/Google/Apple HIG 준수)
//   - AC-4 대비 검산 (WCAG 2.1 AA 4.5:1 기준):
//       brandBlue(#1F6FEB) vs #ffffff = ≈4.6:1 (AA 통과, 버튼 텍스트)
//       textHigh(#14181F) vs #ffffff = ≈16:1 (AAA)
//       textMid(#586069) vs #ffffff = ≈5.7:1 (AAA)
//       textLow(#656C75) vs #ffffff = 5.31:1, vs surface1(#F6F8FB) = 4.99:1 (AA 충족 — 2026-09-18 상향)
//       brandBlue(#1F6FEB) vs brandBeige2(#FAFBFC) = ≈4.5:1 (AA 경계)

// ─── 색상 ───────────────────────────────────────────────────────────────────

export const AUTH_COLORS = {
  // ── OAuth IdP 브랜드 색 (불변 — AC-3·Q-8 보호) ───────────────────────────

  // Kakao — Kakao Developers Brand Guidelines
  kakaoBg: '#FEE500',
  kakaoText: '#191919',
  kakaoShadow: 'rgba(254,229,0,0.4)', // 추천 배지 강조 그림자

  // Naver — Naver 개발자센터 버튼 가이드
  // [WCAG 예외 — 2026-09-18 사용자 결정] #ffffff on #03C75A = 2.25:1 로 WCAG 2.1 AA(4.5:1) 미달이나,
  // 네이버 공식 버튼 가이드가 지정한 조합이라 **그대로 유지**한다(브랜드 식별성 우선).
  // 근거·판정 기록: docs/design-review/2026-09-17_onboarding-landing.md, docs/qa/2026-09-17_contrast-measure.md.
  // 완화: 버튼에 aria-label('네이버로 로그인')이 있어 스크린리더 사용자는 영향 없음. 라벨 색을 임의로 바꾸면
  // 브랜드 가이드 위반이므로, 변경이 필요해지면 네이버 가이드 갱신 여부를 먼저 확인할 것.
  naverBg: '#03C75A',
  naverText: '#ffffff',

  // Google — Google Sign-In 브랜딩 가이드
  googleBg: '#ffffff',
  googleText: '#3c4043',
  googleBorder: '#dadce0',

  // Apple — Sign in with Apple HIG
  appleBg: '#000000',
  appleText: '#ffffff',

  // ── Cool Blue 브랜드 팔레트 (reauth-redesign 2026-05-16) ─────────────────

  // 브랜드 메인 — #1F6FEB (강원캔버스 시그니처 블루)
  brandBlue: '#1F6FEB',
  brandBlueDeep: '#1A4D8F', // 좌측 패널 그라데이션 시작
  brandBlueMid: '#2876D8', // 좌측 패널 그라데이션 중간 (60%)
  brandBlueLight: '#4A8AF0', // CTA 그라데이션 끝
  brandGreen: '#2BB673', // 보조 어센트 (배지·완료 표시)

  // 산 실루엣 / 좌측 패널 텍스처
  mountainShadow1: 'rgba(15,59,112,.6)', // 산 1차 (앞)
  mountainShadow2: 'rgba(15,59,112,.85)', // 산 2차 (뒤)

  // 베이스·서피스
  surface: '#FAF9F7', // 페이지 배경 (편지지 톤)
  surface0: '#FFFFFF', // 폼 패널 / 카드 본체
  surface1: '#F6F8FB', // 글로우 보조 배경 (모바일 AuthBackground)
  surfaceDark: '#14181F', // 다크 텍스트 / 폼 카피 (이전 #14181F 복원)

  // 텍스트 계층 (cool gray)
  textHigh: '#14181F',
  textMid: '#586069',
  // [2026-09-18] 대비 상향(사용자 승인) — 구 #8B949E 는 3.08:1 로 AA 미달인데 약관 안내문·[선택] 라벨·힌트·
  // 글자수 카운터에 사용 중이었다. 신규값은 surface0 위 5.31:1, surface1 위 4.99:1.
  textLow: '#656C75',

  // 구분선·링크
  dividerBase: '#E1E7EF',
  dividerLink: '#586069',

  // ── 알파·오버레이 (좌측 패널·약관·둘러보기) ─────────────────────────────
  whiteAlpha18: 'rgba(255,255,255,.18)', // 로고 박스 backdrop
  whiteAlpha30: 'rgba(255,255,255,.3)', // 로고 박스 border
  // [2026-09-18] .6 은 brandPanel 위 2.6~4.1:1 로 AA 미달 → .85(신규 stop 기준 4.69:1 이상)로 상향
  whiteAlpha60: 'rgba(255,255,255,.85)', // TourAPI 라인(구 .6 — 대비 상향)
  whiteAlpha70: 'rgba(255,255,255,.7)', // 좌측 패널 부카피
  whiteAlpha85: 'rgba(255,255,255,.85)', // 좌측 패널 본카피 보조
  brandShadow: 'rgba(31,111,235,.3)', // CTA 보라색/블루 그림자

  // ── 추천 배지 ────────────────────────────────────────────────────────────
  recommendBadgeBg: '#1F6FEB', // brandBlue (대비 #fff ≈ 4.6:1)
  recommendBadgeText: '#ffffff',

  // ── 소셜 가입 배지 (온보딩 Provider 표시용) ──────────────────────────────
  naverBadgeBg: '#E8F5E9', // 네이버 배지 배경 (연두 파스텔)
  naverBadgeText: '#1A9D4A', // 네이버 배지 텍스트 (대비 #E8F5E9 ≈ 4.6:1 AA 통과)
  googleBadgeBg: '#E8EAED', // 구글 배지 배경 (연회색)
  googleBadgeText: '#444746', // 구글 배지 텍스트 (대비 #E8EAED ≈ 5.2:1 AA 통과)

  // ── 비활성(disabled) ──────────────────────────────────────────────────────
  disabledBg: '#D5DCE5',
  disabledText: '#8B949E',

  // ── 호환 alias (warm 폐기 산물 — 점진 제거) ───────────────────────────────
  brandTealLight: '#2BB673', // 호환: 이전 호출부 grace
  // 폐기 호환 keys (warm 시절 이름 — 호출부 잔존 가능성 대비)
  brandBeige: '#F4EBDE',
  brandCream: '#FBF6EE',
  brandBrown: '#3A2A1F',
  brandBrownMid: '#5A4030',
  brandBrownSoft: '#8A6A52',
  brandGold: '#D9A150',
  brandGoldDeep: '#B8852E',
  panelOverlay: 'rgba(31,111,235,.06)',
} as const;

// ─── 그라데이션 (브랜드 패널 / CTA / 배지) ───────────────────────────────────

export const AUTH_GRADIENTS = {
  // BrandVisualPanel 본체 — 좌상단 BlueDeep → 우하단 BlueMain (60% 지점에 BlueMid)
  // [2026-09-18 대비 상향] 구 중간 stop(#2876D8)·마지막 stop(#1F6FEB) 위에서 흰 알파 텍스트(.85/.60)가
  // 3.78 / 2.63:1 로 AA 미달이었다. 신규 stop 은 흰색 기준 8.39 / 7.27 / 5.85:1 — 알파 .85 에서도 전 구간 4.69:1 이상.
  brandPanel: 'linear-gradient(135deg, #1A4D8F, #1C56A0 60%, #1A5FD0)',

  // 본인 CTA 활성 — 부드러운 블루 그라데이션
  primaryCta: 'linear-gradient(135deg, #1F6FEB, #4A8AF0)',

  // 온보딩 카카오 프로필 사진 옵션 원형 — 시안 gangwonfolio.html:1271 (2026-09-17 OnboardingPage 인라인 hex 이관)
  kakaoAvatar: 'linear-gradient(135deg, #FEE500, #F7B500)',

  // 모바일 AuthBackground 글로우 (두 톤)
  glowBlue: 'radial-gradient(circle, rgba(31,111,235,.15) 0%, transparent 70%)',
  glowGreen: 'radial-gradient(circle, rgba(43,182,115,.1) 0%, transparent 70%)',
} as const;

// ─── 간격·레이아웃 ───────────────────────────────────────────────────────────

export const AUTH_SPACING = {
  // 모바일 카드 최대 폭
  cardMaxWidth: 480,

  // 모바일 카드 내부 패딩
  cardPadding: '56px 28px 32px',

  // OAuth 버튼 스택 gap (px)
  stackGap: '9px',

  // 로고 하단 여백 (모바일)
  logoBelowMb: '22px',

  // 모바일 헤드라인 → 부카피 간격
  headlineToSubMb: '8px',

  // 부카피 → OAuth 스택 간격 (모바일 hero 블록 하단)
  subToStackMb: '48px',

  // 약관 텍스트 상단 여백
  tosTopMt: '28px',

  // BrandVisualPanel 좌우 패딩
  panelPaddingX: '56px',
  panelPaddingY: '48px',

  // 데스크톱 폼 패널 폼 최대 폭
  desktopCardMaxWidth: 380,

  // 데스크톱 폼 패널 좌우 패딩
  desktopPanelPx: '24px',

  // 데스크톱 헤더 → OAuth 스택 간격
  desktopHeaderToStackMb: '36px',

  // 둘러보기 CTA 상단 여백
  anonBrowseMt: '20px',

  // 둘러보기 CTA 하단 여백 (약관 위)
  anonBrowseMb: '8px',
} as const;

// ─── 테두리·라운드 ───────────────────────────────────────────────────────────

export const AUTH_BORDER = {
  // OAuth 버튼 border-radius
  buttonRadius: '11px',

  // CTA 버튼 라운드 (Onboarding 진행 버튼)
  ctaRadius: '13px',

  // 카드/패널 border-radius
  cardRadius: '0px',

  // 로고 박스 (좌측 패널)
  logoBoxRadius: '10px',

  // 포커스 링 두께
  focusRingWidth: '2px',

  // 포커스 링 오프셋
  focusRingOffset: '2px',
} as const;
